import { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom'
import {
  ChevronLeft, ChevronRight, Loader2, Calendar, Building2, Users, FileText,
  ClipboardList, Receipt, MapPin, ExternalLink, Trash2, Save,
  UserPlus, Utensils, Copy, Check, Star, BarChart3, LineChart, Stethoscope,
} from 'lucide-react'
import { api } from '../lib/api.js'
import { fmtDateOnly as dtFmtDateOnly, fmtRange, cmpDateTime } from '../lib/dt.js'
import { isBangkok } from '../lib/ssoArea.js'
import {
  CARD, CARD_BASE, Field, ConfirmDialog, CreatedByChip, DoctorCombobox,
} from '../components/ui.jsx'
import MapPickerModal from '../components/MapPickerModal.jsx'
import SurveyPanel from './_OperationSurveys.jsx'
import DocumentsTab from './_OperationDocuments.jsx'
import PatientListTab from './_OperationPatientList.jsx'
import CstTab from './_OperationCst.jsx'
import TreatmentTab from './_OperationTreatment.jsx'
import ReportTab from './_OperationReport.jsx'
import { CompanyDrawer, CompanyLogoAvatar } from './Companies.jsx'
import { FollowUpDrawer } from './FollowUps.jsx'

const TABS = [
  { slug: 'overview',     label: 'Information',  short: 'Info',     icon: ClipboardList },
  { slug: 'documents',    label: 'Documents',    short: 'Docs',     icon: FileText      },
  { slug: 'patient-list', label: 'Patient list', short: 'Patients', icon: Users         },
  { slug: 'company',      label: 'PR media',     short: 'PR',       icon: Building2     },
  { slug: 'treatment',    label: 'Treatment',    short: 'Income',   icon: Stethoscope   },
  { slug: 'cst',          label: 'CST scores',   short: 'CST',      icon: BarChart3     },
  { slug: 'report',       label: 'Report',       short: 'Report',   icon: LineChart     },
]
const DEFAULT_TAB = 'overview'
const TAB_SLUGS = new Set(TABS.map((t) => t.slug))

const STAGES = ['draft', 'ระหว่างดำเนินการ', 'confirmed', 'complete', 'cancelled']
const STAGE_BADGE = {
  draft:              'bg-zinc-50 ring-zinc-200/70 text-zinc-700',
  'ระหว่างดำเนินการ': 'bg-amber-50 ring-amber-200/80 text-amber-900',
  confirmed:          'bg-blue-50 ring-blue-200/80 text-blue-800',
  complete:           'bg-emerald-50 ring-emerald-200/80 text-emerald-800',
  cancelled:          'bg-rose-50 ring-rose-200/80 text-rose-800',
}
const STAGE_DOT = {
  draft:              'bg-zinc-400',
  'ระหว่างดำเนินการ': 'bg-amber-500',
  confirmed:          'bg-blue-500',
  complete:           'bg-emerald-500',
  cancelled:          'bg-rose-500',
}


export default function OperationDetail() {
  const { id, tab } = useParams()
  const navigate = useNavigate()
  const opId = parseInt(id, 10)
  const activeTab = TAB_SLUGS.has(tab) ? tab : DEFAULT_TAB

  const [op, setOp]       = useState(null)
  const [error, setError] = useState(null)
  const [companyDrawer, setCompanyDrawer] = useState(null)   // company stub { id, name } when drawer should open
  const [fuEditing, setFuEditing]         = useState(null)   // follow-up record (or { __new: true }) when FollowUp drawer should open
  const [lockedCompany, setLockedCompany] = useState(null)   // pre-selected company for new FU created from the CompanyDrawer

  function reload() {
    if (!opId) return
    setError(null)
    api.operations.get(opId).then(setOp).catch((e) => setError(e.message.includes('404') ? 'Operation not found.' : e.message))
  }

  useEffect(() => {
    if (!opId) { setError('bad operation id'); return }
    // Stamp this id so the Operations list highlights it on return.
    sessionStorage.setItem('lastViewedOpId', String(opId))
    setOp(null); reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opId])

  useEffect(() => {
    if (!tab) navigate(`/operations/${opId}/${DEFAULT_TAB}`, { replace: true })
  }, [tab, opId, navigate])

  return (
    <div className="px-4 py-5 md:px-10 md:py-8 max-w-[1400px] mx-auto">
      <Breadcrumb op={op} />

      <header className="mt-3 mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="flex items-center gap-3.5 min-w-0">
          {op?.company_id ? (
            <button
              type="button"
              onClick={() => setCompanyDrawer({ id: op.company_id, name: op.company_name })}
              title="ดูข้อมูลบริษัท"
              className="shrink-0 rounded-xl hover:ring-2 hover:ring-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all"
            >
              <CompanyLogoAvatar name={op.company_name} url={op.company_logo_url || null} size={56} />
            </button>
          ) : (
            <span aria-hidden className="hidden md:block w-1 h-9 rounded-full bg-gradient-to-b from-amber-400 to-orange-500 shrink-0" />
          )}
          <div className="min-w-0">
            <h1 className="text-[22px] md:text-[26px] font-bold tracking-tight leading-tight truncate" title={op?.company_name || ''}>
              {op?.company_name ? (
                <button
                  type="button"
                  onClick={() => setCompanyDrawer({ id: op.company_id, name: op.company_name })}
                  className="text-zinc-900 hover:text-amber-700 transition-colors text-left underline-offset-4 decoration-amber-300/0 hover:decoration-amber-400 hover:underline"
                  title="ดูข้อมูลบริษัท"
                >
                  {op.company_name}
                </button>
              ) : (error ? <span className="text-zinc-900">Operation</span> : <span className="text-zinc-300">Loading…</span>)}
            </h1>
            {op && (
              <div className="text-[13px] text-zinc-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={12} className="text-zinc-400" />
                  <span className="tabular-nums">{fmtRange(op.start_date, op.end_date, op.start_time, op.end_time)}</span>
                </span>
                {op.expected_patients ? (
                  <>
                    <span className="text-zinc-300">·</span>
                    <span className="inline-flex items-center gap-1.5">
                      <Users size={12} className="text-zinc-400" />
                      <span className="tabular-nums">{op.expected_patients} expected</span>
                    </span>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>
        {op && <StageBadge stage={op.stage} />}
      </header>

      <nav className={`${CARD} mb-4 px-1 py-1 flex items-center gap-1 overflow-x-auto`}>
        {TABS.map((t) => {
          const Icon = t.icon
          return (
            <NavLink
              key={t.slug}
              to={`/operations/${opId}/${t.slug}`}
              end
              title={t.label}
              className={({ isActive }) =>
                'inline-flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded-md text-[13px] font-medium whitespace-nowrap transition-colors ' +
                (isActive
                  ? 'bg-amber-500 text-white shadow-sm shadow-amber-500/25'
                  : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50')
              }
            >
              <Icon size={14} strokeWidth={2.25} />
              <span className="hidden md:inline">{t.label}</span>
            </NavLink>
          )
        })}
      </nav>

      {error && (
        <div className="px-4 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-[13px]">{error}</div>
      )}

      {!op && !error
        ? <div className={`${CARD} px-5 py-10 text-center text-zinc-400 text-[13px]`}>
            <Loader2 className="inline-block w-4 h-4 animate-spin mr-1.5" />
            Loading operation…
          </div>
        : op && (
          activeTab === 'company' ? (
            <CompanyTab op={op} />
          ) : activeTab === 'overview' ? (
            <OverviewEditor
              op={op}
              onSaved={reload}
              onDeleted={() => navigate('/operations')}
            />
          ) : activeTab === 'documents' ? (
            <DocumentsTab op={op} />
          ) : activeTab === 'patient-list' ? (
            <PatientListTab op={op} />
          ) : activeTab === 'treatment' ? (
            <TreatmentTab op={op} />
          ) : activeTab === 'cst' ? (
            <CstTab op={op} />
          ) : activeTab === 'report' ? (
            <ReportTab op={op} />
          ) : null
        )}

      <CompanyDrawer
        company={companyDrawer}
        onClose={() => setCompanyDrawer(null)}
        onSaved={() => { setCompanyDrawer(null); reload() }}
        onDeleted={() => { setCompanyDrawer(null); navigate('/operations') }}
        onAddFollowUp={(co) => { setLockedCompany(co); setFuEditing({ __new: true }) }}
        onEditFollowUp={(fu, co) => { setLockedCompany(co); setFuEditing(fu) }}
        zClass="z-40"
      />

      <FollowUpDrawer
        followUp={fuEditing}
        lockedCompany={lockedCompany}
        onClose={() => { setFuEditing(null); setLockedCompany(null) }}
        onSaved={() => {
          setFuEditing(null); setLockedCompany(null)
          // Refresh the open CompanyDrawer so the new FU shows up in its history.
          if (companyDrawer?.id) {
            const id = companyDrawer.id
            setCompanyDrawer(null)
            setTimeout(() => api.companies.get(id).then(setCompanyDrawer).catch(() => {}), 50)
          }
        }}
        onDeleted={() => {
          setFuEditing(null); setLockedCompany(null)
          if (companyDrawer?.id) {
            const id = companyDrawer.id
            setCompanyDrawer(null)
            setTimeout(() => api.companies.get(id).then(setCompanyDrawer).catch(() => {}), 50)
          }
        }}
        onAddAnother={(co) => {
          setLockedCompany({ id: co.id, name: co.name })
          setFuEditing({ __new: true, company_id: co.id, company_name: co.name })
        }}
      />
    </div>
  )
}


function Breadcrumb({ op }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[12.5px] text-zinc-500">
      <Link to="/operations" className="inline-flex items-center gap-1 hover:text-zinc-900 px-1 py-0.5 -ml-1 rounded">
        <ChevronLeft size={13} strokeWidth={2.25} className="text-zinc-400" />
        <span className="font-medium">Operation</span>
      </Link>
      <ChevronRight size={11} className="text-zinc-300" />
      <span className="text-zinc-700 inline-flex items-center gap-1 truncate min-w-0">
        <Building2 size={11} className="text-zinc-400 shrink-0" />
        <span className="truncate">{op?.company_name || '…'}</span>
        {op?.start_date ? <span className="text-zinc-300 ml-1">·</span> : null}
        {op?.start_date ? <span className="tabular-nums shrink-0">{dtFmtDateOnly(op.start_date)}</span> : null}
      </span>
    </nav>
  )
}


/* ──────────────────────────────── Overview editor ─────────────────────────── */

function OverviewEditor({ op, onSaved, onDeleted }) {
  const navigate = useNavigate()
  const initial = useMemo(() => toForm(op), [op])
  const [form, setForm]     = useState(initial)
  const [saving, setSaving] = useState(false)
  const [err, setErr]       = useState(null)
  const [mapOpen, setMapOpen]         = useState(false)
  const [deleteOpen, setDeleteOpen]   = useState(false)

  // Reset local form when the underlying op changes (after a reload).
  useEffect(() => { setForm(initial); setErr(null) }, [initial])

  const dirty = !shallowEqual(form, initial)

  async function save() {
    if (saving) return
    setErr(null)
    const { startDate, endDate, startTime, endTime } = form
    if (!startDate || !endDate)   { setErr('กรุณาระบุวันที่เริ่มและสิ้นสุด'); return }
    if (!startTime || !endTime)   { setErr('กรุณาระบุเวลาเริ่มและสิ้นสุด'); return }
    if (endDate < startDate)      { setErr('วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่ม'); return }
    if (cmpDateTime(endDate, endTime, startDate, startTime) <= 0) {
      setErr('เวลาสิ้นสุดต้องอยู่หลังเวลาเริ่ม'); return
    }
    const doctorDaysPayload = effectiveDoctorDays(form)
    // Validate each day's pair: doctor1 ≠ doctor2 (catches it before the API does).
    const perDay = new Map()
    for (const r of doctorDaysPayload) {
      const b = perDay.get(r.day_date) || {}
      b[`slot${r.slot}`] = r.doctor_xano_id
      perDay.set(r.day_date, b)
    }
    for (const [day, b] of perDay) {
      if (b.slot1 != null && b.slot2 != null && b.slot1 === b.slot2) {
        setErr(`${day}: Doctor 1 และ Doctor 2 ต้องไม่ใช่คนเดียวกัน`); return
      }
    }
    setSaving(true)
    try {
      await api.operations.update(op.id, {
        start_date:            startDate,
        end_date:              endDate,
        start_time:            startTime,
        end_time:              endTime,
        expected_patients:     form.expected === '' ? null : parseInt(form.expected, 10),
        stage:                 form.stage,
        location_address:      form.locAddress  || null,
        location_province:     form.locProvince || null,
        location_district:     form.locDistrict || null,
        location_sso_district: form.locSso      || null,
        location_gps_lat:      form.lat,
        location_gps_lng:      form.lng,
        location_map_url:      form.mapUrl      || null,
        lunch_by_company:      form.lunchByCompany,           // null | 0 | 1
        lunch_arrangement:     form.lunchNote   || null,
        doctor_days:           doctorDaysPayload,
      })
      onSaved()
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setSaving(false)
    }
  }

  async function doDelete() {
    await api.operations.remove(op.id)
    setDeleteOpen(false)
    onDeleted()
  }

  function applyMap({ lat, lng, address, map_url }) {
    setForm((f) => ({
      ...f,
      lat, lng, mapUrl: map_url,
      locAddress: f.locAddress || address || '',
    }))
    setMapOpen(false)
  }

  return (
    <div className="space-y-4">
      <div className={`${CARD} p-5 md:p-6`}>
        <SectionHeader title="ภาพรวม" subtitle="ปรับแก้รายละเอียดของ Operation นี้ (บริษัทเปลี่ยนไม่ได้)" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Company" hintInline="เปลี่ยนไม่ได้">
            <div className="px-2.5 py-1.5 rounded-lg ring-1 ring-zinc-200 bg-zinc-50 text-[13.5px] text-zinc-700 truncate" title={op.company_name}>
              {op.company_name}
            </div>
          </Field>

          <Field label="Stage">
            <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}
              className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none">
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>

          <Field label="วันที่เริ่ม">
            <input type="date" value={form.startDate}
              onChange={(e) => {
                const v = e.target.value
                // Keep end_date ≥ start_date: bump the end up if it now trails.
                setForm((f) => ({ ...f, startDate: v, endDate: f.endDate && f.endDate < v ? v : f.endDate }))
              }}
              className="w-full pl-2.5 pr-8 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
          </Field>

          <Field label="วันที่สิ้นสุด">
            <input type="date" value={form.endDate} min={form.startDate || undefined}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              className="w-full pl-2.5 pr-8 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
          </Field>

          <Field label="เวลาเริ่ม">
            <input type="time" value={form.startTime}
              onChange={(e) => setForm({ ...form, startTime: e.target.value })}
              className="w-full pl-2.5 pr-8 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
          </Field>

          <Field label="เวลาสิ้นสุด">
            <input type="time" value={form.endTime}
              onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              className="w-full pl-2.5 pr-8 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
          </Field>

          <Field label="จำนวนคนไข้คาดการณ์">
            <input type="number" min={0} value={form.expected ?? ''}
              onChange={(e) => setForm({ ...form, expected: e.target.value })}
              placeholder="—"
              className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
          </Field>

          <Field label="Created">
            <div className="flex items-center gap-2">
              <CreatedByChip
                createdAtUnix={op.created_at}
                createdById={op.created_by_xano_id}
              />
              <span className="text-[12px] text-zinc-400 tabular-nums">{fmtFull(op.created_at)}</span>
            </div>
          </Field>
        </div>
      </div>

      {/* Location */}
      <div className={`${CARD} p-5 md:p-6`}>
        <SectionHeader title="สถานที่ออกหน่วย" subtitle="ค่าเริ่มต้นมาจากที่อยู่ของบริษัท แก้ไขได้ตามจริง" />
        <div className="space-y-3">
          <Field label="ที่อยู่">
            <textarea rows={2} value={form.locAddress}
              onChange={(e) => setForm({ ...form, locAddress: e.target.value })}
              className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none resize-y"
              placeholder="เลขที่ / ถนน / แขวง / เขต / จังหวัด / รหัสไปรษณีย์" />
          </Field>
          <div className={'grid grid-cols-1 gap-3 ' + (isBangkok(form.locProvince) ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
            <Field label="จังหวัด">
              <input type="text" value={form.locProvince}
                onChange={(e) => setForm({ ...form, locProvince: e.target.value })}
                placeholder="—"
                className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
            </Field>
            {isBangkok(form.locProvince) && (
              <Field label="อำเภอ / เขต">
                <input type="text" value={form.locDistrict}
                  onChange={(e) => setForm({ ...form, locDistrict: e.target.value })}
                  placeholder="—"
                  className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
              </Field>
            )}
            <Field label="ประกันสังคมเขต">
              <input type="text" value={form.locSso}
                onChange={(e) => setForm({ ...form, locSso: e.target.value })}
                placeholder="—"
                className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => setMapOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ring-1 ring-zinc-200 bg-white hover:bg-zinc-50 text-[13px] text-zinc-700">
              <MapPin size={13} className="text-amber-500" />
              {form.mapUrl ? 'แก้ไขตำแหน่งบนแผนที่' : 'ปักหมุดบนแผนที่'}
            </button>
            {form.lat != null && form.lng != null && (
              <span className="text-[12px] text-zinc-500 tabular-nums">
                {form.lat.toFixed(5)}, {form.lng.toFixed(5)}
              </span>
            )}
            {form.mapUrl && (
              <a href={form.mapUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[12.5px] text-amber-700 hover:text-amber-800">
                เปิดใน Google Maps <ExternalLink size={11} />
              </a>
            )}
            {form.mapUrl && (
              <CopyButton text={form.mapUrl} />
            )}
            {form.mapUrl && (
              <button type="button" onClick={() => setForm({ ...form, lat: null, lng: null, mapUrl: null })}
                className="text-[12.5px] text-zinc-400 hover:text-rose-600">ลบหมุด</button>
            )}
          </div>
        </div>
      </div>

      {/* Contacts (subsection) */}
      <OperationContactsPanel
        opId={op.id}
        companyId={op.company_id}
        contacts={op.contacts || []}
        onChanged={onSaved}
      />

      {/* Doctors — CARD_BASE (no overflow-hidden) so the dentist dropdown
          isn't clipped by the card boundary. */}
      <div className={`${CARD_BASE} p-5 md:p-6`}>
        <SectionHeader title="ทันตแพทย์ออกตรวจ" subtitle="Tooth Taxi มีเก้าอี้ 2 ตัว — ถ้าคนไข้ > 25–30 คน/วัน แนะนำเปิดเก้าอี้ 2" />
        <DoctorAssignmentsSection form={form} setForm={setForm} />
      </div>

      {/* Misc — lunch arrangement (other operational notes will land here) */}
      <div className={`${CARD} p-5 md:p-6`}>
        <SectionHeader title="อื่นๆ" subtitle="อาหารกลางวันและบันทึกเพิ่มเติม" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="อาหารกลางวัน" hintInline="บริษัทเตรียมให้หรือไม่">
            <LunchToggle value={form.lunchByCompany} onChange={(v) => setForm({ ...form, lunchByCompany: v })} />
          </Field>
          <Field label="หมายเหตุเรื่องอาหาร">
            <input type="text" value={form.lunchNote}
              onChange={(e) => setForm({ ...form, lunchNote: e.target.value })}
              placeholder="เช่น เมนู, ผู้ประสานงาน"
              className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none" />
          </Field>
        </div>
      </div>

      {/* Danger zone */}
      <div className={`${CARD} p-5 md:p-6 ring-rose-200/60`}>
        <SectionHeader title="Danger zone" subtitle="ลบ Operation นี้ถาวร (ติดต่อ + เอกสารแนบจะถูกลบไปด้วย)" />
        <button type="button" onClick={() => setDeleteOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] text-rose-700 hover:bg-rose-50 ring-1 ring-rose-200">
          <Trash2 size={13} /> ลบ Operation
        </button>
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-2 z-20 flex justify-end pointer-events-none">
        <div className={`pointer-events-auto ${CARD} px-3 py-2 inline-flex items-center gap-3 transition-opacity ${dirty ? 'opacity-100' : 'opacity-0'}`}>
          {err ? <span className="text-[12.5px] text-rose-600">{err}</span>
               : <span className="text-[12.5px] text-zinc-500">มีการแก้ไขที่ยังไม่บันทึก</span>}
          <button onClick={() => setForm(initial)} disabled={saving}
            className="text-[12.5px] text-zinc-500 hover:text-zinc-900 px-2 py-1 rounded-md">ยกเลิก</button>
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-medium disabled:opacity-60">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} บันทึก
          </button>
        </div>
      </div>

      {mapOpen && (
        <MapPickerModal
          initial={{ lat: form.lat, lng: form.lng, address: form.locAddress }}
          onClose={() => setMapOpen(false)}
          onSave={applyMap}
        />
      )}

      {/* HR Report card lives here — it's a top-level operation deliverable,
          not a PR or CST artifact. */}
      <SurveyPanel opId={op.id} op={op} cards={['report']} />

      <ConfirmDialog
        open={deleteOpen}
        title="ลบ Operation นี้?"
        message="ข้อมูลทั้งหมดที่เกี่ยวข้อง (ติดต่อ, เอกสาร) จะถูกลบไปด้วย — กู้คืนไม่ได้"
        confirmLabel="ลบถาวร"
        cancelLabel="ยกเลิก"
        tone="danger"
        onCancel={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  )
}


/* ──────────────────────────────── Operation contacts panel ──────────────── */

function OperationContactsPanel({ opId, companyId, contacts, onChanged }) {
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)   // contact id

  return (
    <div className={`${CARD} p-5 md:p-6`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <SectionHeader title="ผู้ประสานงานสำหรับ Operation นี้" subtitle="เลือกจากผู้ติดต่อของบริษัท หรือเพิ่มใหม่เฉพาะงานนี้" compact />
        <button type="button" onClick={() => { setAdding(true); setEditing(null) }}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ring-1 ring-zinc-200 hover:bg-zinc-50 text-[13px] text-zinc-700">
          <UserPlus size={13} /> เพิ่มผู้ติดต่อ
        </button>
      </div>

      {contacts.length === 0 && !adding && (
        <div className="text-[12.5px] text-zinc-400 italic">ยังไม่มีผู้ประสานงาน</div>
      )}

      <ul className="space-y-2">
        {contacts.map((c) =>
          editing === c.id ? (
            <li key={c.id}>
              <ContactEditor
                opId={opId} companyId={companyId} contact={c}
                onDone={() => { setEditing(null); onChanged() }}
                onCancel={() => setEditing(null)}
              />
            </li>
          ) : (
            <li key={c.id}>
              <ContactCard contact={c}
                onEdit={() => { setEditing(c.id); setAdding(false) }}
                onDelete={async () => { await api.operations.contacts.remove(opId, c.id); onChanged() }}
                onSetPrimary={async () => { await api.operations.contacts.update(opId, c.id, { is_primary: true }); onChanged() }}
              />
            </li>
          )
        )}
        {adding && (
          <li>
            <ContactEditor
              opId={opId} companyId={companyId}
              onDone={() => { setAdding(false); onChanged() }}
              onCancel={() => setAdding(false)}
            />
          </li>
        )}
      </ul>
    </div>
  )
}

function ContactCard({ contact, onEdit, onDelete, onSetPrimary }) {
  const isPrimary = !!contact.is_primary
  return (
    <div className={'flex items-center gap-3 px-3 py-2 rounded-lg ring-1 bg-white ' +
      (isPrimary ? 'ring-amber-300/70 bg-amber-50/30' : 'ring-zinc-200/70')}>
      <button
        type="button"
        onClick={isPrimary ? undefined : onSetPrimary}
        title={isPrimary ? 'ผู้ติดต่อหลัก' : 'ตั้งเป็นผู้ติดต่อหลัก'}
        aria-label={isPrimary ? 'Primary' : 'Set as primary'}
        className={'w-7 h-7 rounded-full inline-flex items-center justify-center transition-colors shrink-0 ' +
          (isPrimary
            ? 'text-amber-500 cursor-default'
            : 'text-zinc-300 hover:text-amber-400 hover:bg-zinc-50')}
      >
        <Star size={14} strokeWidth={2} fill={isPrimary ? 'currentColor' : 'none'} />
      </button>
      <div className="w-8 h-8 rounded-full bg-amber-50 text-amber-700 inline-flex items-center justify-center text-[12.5px] font-semibold shrink-0">
        {(contact.name || '?').slice(0, 1)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-medium text-zinc-900 truncate">
          {contact.name}
          {contact.role && <span className="ml-2 text-[11.5px] text-zinc-500 font-normal">{contact.role}</span>}
        </div>
        {contact.fullname && (
          <div className="text-[11.5px] text-zinc-500 truncate" title="ชื่อ-สกุล สำหรับเอกสาร">
            {contact.fullname}
          </div>
        )}
        <div className="text-[12px] text-zinc-500 flex items-center gap-x-3 gap-y-0.5 flex-wrap">
          {contact.phone && <span className="tabular-nums">{contact.phone}</span>}
          {contact.email && <span className="truncate">{contact.email}</span>}
          {contact.line_id && <span>LINE: {contact.line_id}</span>}
          {contact.company_contact_id && <span className="text-[10.5px] uppercase tracking-wider text-zinc-400">from company</span>}
        </div>
      </div>
      <button type="button" onClick={onEdit} className="text-[12px] text-zinc-500 hover:text-zinc-900 px-2 py-1 rounded">แก้ไข</button>
      <button type="button" onClick={onDelete} className="text-[12px] text-zinc-400 hover:text-rose-600 px-2 py-1 rounded">ลบ</button>
    </div>
  )
}

function ContactEditor({ opId, companyId, contact, onDone, onCancel }) {
  const isNew = !contact
  const [name,     setName]     = useState(contact?.name     || '')
  const [fullname, setFullname] = useState(contact?.fullname || '')
  const [phone,    setPhone]    = useState(contact?.phone    || '')
  const [email,    setEmail]    = useState(contact?.email    || '')
  const [lineId,   setLineId]   = useState(contact?.line_id  || '')
  const [role,     setRole]     = useState(contact?.role     || '')
  const [pick,    setPick]    = useState(false)
  const [companyContacts, setCompanyContacts] = useState(null)
  const [pickedId, setPickedId] = useState(contact?.company_contact_id || null)
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState(null)

  useEffect(() => {
    if (!pick || companyContacts != null) return
    api.companies.get(companyId).then((c) => setCompanyContacts(c.contacts || [])).catch(() => setCompanyContacts([]))
  }, [pick, companyId, companyContacts])

  async function save() {
    setErr(null)
    if (!name.trim()) { setErr('ชื่อจำเป็น'); return }
    setBusy(true)
    try {
      const payload = {
        name: name.trim(), fullname: fullname.trim() || null,
        phone: phone.trim() || null, email: email.trim() || null,
        line_id: lineId.trim() || null, role: role.trim() || null,
        company_contact_id: pickedId || null,
      }
      if (isNew) await api.operations.contacts.add(opId, payload)
      else       await api.operations.contacts.update(opId, contact.id, payload)
      onDone()
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setBusy(false)
    }
  }

  function pickContact(c) {
    setPickedId(c.id)
    setName(c.name || ''); setFullname(c.fullname || '')
    setPhone(c.phone || ''); setEmail(c.email || '')
    setLineId(c.line_id || ''); setRole(c.role || '')
    setPick(false)
  }

  return (
    <div className="rounded-lg ring-1 ring-amber-200/70 bg-amber-50/30 p-3 space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">
          {isNew ? 'เพิ่มผู้ติดต่อใหม่' : 'แก้ไขผู้ติดต่อ'}
        </div>
        <button type="button" onClick={() => setPick((p) => !p)}
          className="ml-auto text-[12px] text-amber-700 hover:text-amber-800">
          {pick ? 'พิมพ์เอง' : 'เลือกจากผู้ติดต่อบริษัท'}
        </button>
      </div>

      {pick ? (
        <div className="border border-zinc-200 rounded-md bg-white max-h-48 overflow-y-auto">
          {companyContacts == null && (
            <div className="px-3 py-3 text-[12.5px] text-zinc-400 text-center"><Loader2 size={12} className="inline animate-spin mr-1" />Loading…</div>
          )}
          {companyContacts && companyContacts.length === 0 && (
            <div className="px-3 py-3 text-[12.5px] text-zinc-400 text-center">บริษัทยังไม่มีผู้ติดต่อ</div>
          )}
          {companyContacts && companyContacts.map((c) => (
            <button key={c.id} type="button" onClick={() => pickContact(c)}
              className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-amber-50 flex items-center justify-between gap-2">
              <span className="min-w-0">
                <span className="font-medium">{c.name}</span>
                {c.role && <span className="ml-2 text-[11.5px] text-zinc-500">{c.role}</span>}
                {c.fullname && <div className="text-[11px] text-zinc-500 truncate">{c.fullname}</div>}
              </span>
              <span className="text-[11.5px] text-zinc-400 tabular-nums shrink-0">{c.phone || ''}</span>
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="ชื่อเรียก (จำเป็น)"
              className="px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-amber-400 focus:outline-none text-[13px] bg-white" />
            <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="ตำแหน่ง (HR, ผู้ประสานงาน, …)"
              className="px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-amber-400 focus:outline-none text-[13px] bg-white" />
            <input value={fullname} onChange={(e) => setFullname(e.target.value)} placeholder="ชื่อ-สกุล (สำหรับเอกสารราชการ)"
              className="px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-amber-400 focus:outline-none text-[13px] bg-white sm:col-span-2" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="เบอร์โทร"
              className="px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-amber-400 focus:outline-none text-[13px] bg-white" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="อีเมล"
              className="px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-amber-400 focus:outline-none text-[13px] bg-white" />
            <input value={lineId} onChange={(e) => setLineId(e.target.value)} placeholder="LINE ID"
              className="px-2.5 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-amber-400 focus:outline-none text-[13px] bg-white sm:col-span-2" />
          </div>
        </>
      )}

      {err && <div className="text-[12px] text-rose-600">{err}</div>}
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} disabled={busy} className="px-2.5 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100">ยกเลิก</button>
        <button type="button" onClick={save} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[12.5px] font-medium disabled:opacity-60">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} บันทึก
        </button>
      </div>
    </div>
  )
}


/* ──────────────────────────────── small bits ──────────────────────────────── */

function CopyButton({ text, label = 'คัดลอกลิงก์' }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500) }
    catch { /* clipboard blocked — silently noop */ }
  }
  return (
    <button type="button" onClick={copy}
      className="inline-flex items-center gap-1 text-[12.5px] text-zinc-500 hover:text-amber-700 px-1.5 py-0.5 rounded">
      {copied ? <><Check size={11} className="text-emerald-600" /> คัดลอกแล้ว</> : <><Copy size={11} /> {label}</>}
    </button>
  )
}

function LunchToggle({ value, onChange }) {
  const opts = [
    { v: 1,    label: 'บริษัทเตรียมให้', cls: 'bg-emerald-50 text-emerald-800 ring-emerald-200' },
    { v: 0,    label: 'staff หาเอง',     cls: 'bg-rose-50 text-rose-800 ring-rose-200' },
    { v: null, label: '—',               cls: 'bg-zinc-50 text-zinc-600 ring-zinc-200' },
  ]
  return (
    <div className="inline-flex gap-1">
      {opts.map((o) => (
        <button key={String(o.v)} type="button" onClick={() => onChange(o.v)}
          className={'px-2.5 py-1 rounded-md ring-1 text-[12.5px] font-medium transition-colors ' +
            (value === o.v ? o.cls : 'bg-white text-zinc-500 ring-zinc-200 hover:bg-zinc-50')}>
          <span className="inline-flex items-center gap-1.5">
            {o.v !== null && <Utensils size={11} />}
            {o.label}
          </span>
        </button>
      ))}
    </div>
  )
}

function DoctorAssignmentsSection({ form, setForm }) {
  const days = enumerateDays(form.startDate, form.endDate)

  if (!days.length) {
    return (
      <div className="text-[13px] text-zinc-500 bg-zinc-50/60 ring-1 ring-zinc-200 rounded-lg px-3 py-2.5">
        ระบุวันที่เริ่ม/สิ้นสุดก่อน ระบบจะให้เลือกทันตแพทย์ตามวัน
      </div>
    )
  }

  const multiDay = days.length > 1
  const day1Key  = days[0]

  function patchDay(day, slot, value) {
    const next = { ...form.doctorDays }
    next[day] = { ...(next[day] || { slot1: null, slot2: null }) }
    next[day][`slot${slot}`] = value
    setForm({ ...form, doctorDays: next })
  }

  function toggleSameEveryDay(checked) {
    // Going OFF: seed every day with day-1's pair so the user has a baseline
    // to edit. Going ON: leave per-day state untouched — save will cascade.
    if (!checked) {
      const day1 = form.doctorDays[day1Key] || { slot1: null, slot2: null }
      const next = { ...form.doctorDays }
      for (const d of days) {
        if (d === day1Key) continue
        next[d] = { slot1: day1.slot1, slot2: day1.slot2 }
      }
      setForm({ ...form, sameEveryDay: false, doctorDays: next })
    } else {
      setForm({ ...form, sameEveryDay: true })
    }
  }

  // Multi-day + "same every day": single picker pair, same as single-day UX
  // but with a small note that it applies to every day.
  if (multiDay && form.sameEveryDay) {
    return (
      <div className="space-y-3">
        <label className="inline-flex items-start gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={true} onChange={(e) => toggleSameEveryDay(e.target.checked)}
            className="accent-amber-500 w-3.5 h-3.5 mt-0.5" />
          <span className="text-[12.5px] text-zinc-800 leading-snug">
            ใช้ทันตแพทย์ชุดเดียวกันทุกวัน
            <span className="block text-[11px] text-zinc-500">{days.length} วัน · ทุกวันใช้ Doctor 1/2 ชุดเดียวกัน</span>
          </span>
        </label>
        <DoctorPairRow
          slot1={form.doctorDays[day1Key]?.slot1 ?? null}
          slot2={form.doctorDays[day1Key]?.slot2 ?? null}
          onChange1={(v) => patchDay(day1Key, 1, v)}
          onChange2={(v) => patchDay(day1Key, 2, v)}
        />
      </div>
    )
  }

  // Single-day: just one pair, no extra UI.
  if (!multiDay) {
    return (
      <DoctorPairRow
        slot1={form.doctorDays[day1Key]?.slot1 ?? null}
        slot2={form.doctorDays[day1Key]?.slot2 ?? null}
        onChange1={(v) => patchDay(day1Key, 1, v)}
        onChange2={(v) => patchDay(day1Key, 2, v)}
      />
    )
  }

  // Multi-day + per-day mode: one picker pair per day.
  return (
    <div className="space-y-4">
      <label className="inline-flex items-start gap-2 cursor-pointer select-none">
        <input type="checkbox" checked={false} onChange={(e) => toggleSameEveryDay(e.target.checked)}
          className="accent-amber-500 w-3.5 h-3.5 mt-0.5" />
        <span className="text-[12.5px] text-zinc-800 leading-snug">
          ใช้ทันตแพทย์ชุดเดียวกันทุกวัน
          <span className="block text-[11px] text-zinc-500">ตอนนี้กำหนดแยกราย {days.length} วัน</span>
        </span>
      </label>
      <ul className="divide-y divide-zinc-100">
        {days.map((d) => {
          const cur = form.doctorDays[d] || { slot1: null, slot2: null }
          return (
            <li key={d} className="py-3 first:pt-0 last:pb-0">
              <div className="text-[12px] font-semibold text-zinc-700 mb-2 tabular-nums">{dtFmtDateOnly(d)}</div>
              <DoctorPairRow
                slot1={cur.slot1}
                slot2={cur.slot2}
                onChange1={(v) => patchDay(d, 1, v)}
                onChange2={(v) => patchDay(d, 2, v)}
              />
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function DoctorPairRow({ slot1, slot2, onChange1, onChange2 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Doctor 1" hintInline="เก้าอี้ที่ 1">
        <DoctorCombobox value={slot1} onChange={onChange1} disabledIds={[slot2]} />
      </Field>
      <Field label="Doctor 2" hintInline="เก้าอี้ที่ 2">
        <DoctorCombobox value={slot2} onChange={onChange2} disabledIds={[slot1]} />
      </Field>
    </div>
  )
}

function SectionHeader({ title, subtitle, compact = false }) {
  return (
    <div className={compact ? 'mb-2' : 'mb-4'}>
      <h2 className="text-[14px] font-semibold text-zinc-900">{title}</h2>
      {subtitle && <p className="text-[12px] text-zinc-500 mt-0.5">{subtitle}</p>}
    </div>
  )
}

/* ──────────────────────────────── Company tab ───────────────────────────── */

function CompanyTab({ op }) {
  const navigate = useNavigate()
  const [co, setCo]       = useState(null)
  const [error, setError]  = useState(null)

  useEffect(() => {
    if (!op?.company_id) return
    let cancelled = false
    setCo(null); setError(null)
    api.companies.get(op.company_id)
      .then((d) => { if (!cancelled) setCo(d) })
      .catch((e) => { if (!cancelled) setError(e.message) })
    return () => { cancelled = true }
  }, [op?.company_id])

  if (error) return <div className={`${CARD} px-5 py-6 text-rose-700 bg-rose-50 ring-rose-200`}>{error}</div>
  if (!co)   return <div className={`${CARD} px-5 py-10 text-center text-zinc-400`}><Loader2 className="inline w-4 h-4 animate-spin mr-1.5" /> Loading…</div>

  const otherOps = (co.operations || []).filter((o) => o.id !== op.id)

  return (
    <div className="space-y-4">
      {/* Past operations (other than this one) */}
      {otherOps.length > 0 && (
        <div className={`${CARD} p-5 md:p-6`}>
          <h3 className="text-[14px] font-semibold text-zinc-900 mb-2">Past operations <span className="text-zinc-400 font-normal text-[12px]">({otherOps.length})</span></h3>
          <ul className="space-y-1.5">
            {otherOps.map((o) => (
              <li key={o.id}>
                <button type="button" onClick={() => navigate(`/operations/${o.id}`)}
                  className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg ring-1 ring-zinc-200/70 hover:ring-amber-300 hover:bg-amber-50/40 transition-colors">
                  <Calendar size={13} className="text-amber-500 shrink-0" />
                  <span className="text-[12.5px] tabular-nums font-medium text-zinc-900">{fmtRange(o.start_date, o.end_date, o.start_time, o.end_time)}</span>
                  {o.expected_patients ? <span className="text-[12px] text-zinc-500">· N {o.expected_patients}</span> : null}
                  <span className={'ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-medium ring-1 ' + (STAGE_BADGE[o.stage] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700')}>
                    {o.stage}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* PR-related cards only — HR Report lives on Information, CST card
          (link + summary) lives on the CST scores tab. The CST QR poster
          stays here with the other PR artwork. Masonry layout packs the
          variable-height image-preview cards efficiently. */}
      <SurveyPanel opId={op.id} op={op} cards={['pr-material', 'pr-banner', 'cst-banner']} layout="masonry" />
    </div>
  )
}

function StageBadge({ stage }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md ring-1 text-[11.5px] font-medium ${STAGE_BADGE[stage] || 'bg-zinc-50 ring-zinc-200/70 text-zinc-700'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${STAGE_DOT[stage] || 'bg-zinc-400'}`} />
      {stage}
    </span>
  )
}

function SectionStub({ title, nextStep }) {
  return (
    <div className="py-10 text-center">
      <div className="text-zinc-300 text-[36px] leading-none mb-2">·</div>
      <h2 className="text-[16px] font-semibold text-zinc-900">{title}</h2>
      <p className="mt-1 text-[12.5px] text-zinc-500">Not built yet · <span className="text-zinc-700">{nextStep}</span></p>
    </div>
  )
}


/* ──────────────────────────────── form / time helpers ─── */

function toForm(op) {
  return {
    stage:           op.stage,
    startDate:       op.start_date || '',
    endDate:         op.end_date   || '',
    startTime:       op.start_time || '',
    endTime:         op.end_time   || '',
    expected:        op.expected_patients == null ? '' : String(op.expected_patients),
    locAddress:      op.location_address || '',
    locProvince:     op.location_province || '',
    locDistrict:     op.location_district || '',
    locSso:          op.location_sso_district || '',
    lat:             op.location_gps_lat ?? null,
    lng:             op.location_gps_lng ?? null,
    mapUrl:          op.location_map_url || '',
    lunchByCompany:  op.lunch_by_company ?? null,    // null | 0 | 1
    lunchNote:       op.lunch_arrangement || '',
    // Per-day doctor assignments: { [day_date]: { slot1: id|null, slot2: id|null } }.
    // `sameEveryDay` is purely UI state: when true (multi-day only), day 1's
    // values cascade to every day on save regardless of `doctorDays`.
    doctorDays:      groupDoctorDays(op.doctor_days || []),
    sameEveryDay:    detectSameEveryDay(op.doctor_days || [], op.start_date, op.end_date),
  }
}

function groupDoctorDays(rows) {
  const out = {}
  for (const r of rows) {
    if (!r?.day_date || (r.slot !== 1 && r.slot !== 2)) continue
    out[r.day_date] ??= { slot1: null, slot2: null }
    out[r.day_date][`slot${r.slot}`] = r.doctor_xano_id ?? null
  }
  return out
}

// Default the "use the same doctors every day" toggle to true unless the
// stored data clearly varies per day. New ops (no assignments yet) default
// to true so the multi-day UI starts in the simpler one-picker mode.
function detectSameEveryDay(rows, startDate, endDate) {
  if (!startDate || !endDate || startDate === endDate) return false   // single-day → toggle irrelevant
  const days = enumerateDays(startDate, endDate)
  const grouped = groupDoctorDays(rows)
  const day1 = grouped[days[0]] || { slot1: null, slot2: null }
  for (let i = 1; i < days.length; i++) {
    const d = grouped[days[i]]
    if (!d) continue                                  // missing day → assume "same"
    if (d.slot1 !== day1.slot1 || d.slot2 !== day1.slot2) return false
  }
  return true
}

// Enumerate every YYYY-MM-DD between start and end inclusive. Capped at 31
// to defend against fat-finger year typos that would otherwise blow up the UI.
function enumerateDays(startDate, endDate) {
  if (!startDate || !endDate || endDate < startDate) return []
  const days = []
  let cur = startDate
  for (let i = 0; cur <= endDate && i < 31; i++) {
    days.push(cur)
    cur = addOneDay(cur)
  }
  return days
}
function addOneDay(s) {
  const [y, m, d] = s.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + 1)
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`
}

// Resolve form state → the doctor_days array we POST. Day 1 cascades to every
// day when `sameEveryDay` is on (multi-day ops only).
function effectiveDoctorDays(form) {
  const days = enumerateDays(form.startDate, form.endDate)
  if (!days.length) return []
  const multiDay = days.length > 1
  const cascade  = multiDay && form.sameEveryDay
  const day1     = form.doctorDays[days[0]] || { slot1: null, slot2: null }
  const rows = []
  for (const d of days) {
    const src = cascade ? day1 : (form.doctorDays[d] || { slot1: null, slot2: null })
    if (src.slot1 != null) rows.push({ day_date: d, slot: 1, doctor_xano_id: src.slot1 })
    if (src.slot2 != null) rows.push({ day_date: d, slot: 2, doctor_xano_id: src.slot2 })
  }
  return rows
}

function shallowEqual(a, b) {
  for (const k of Object.keys(a)) if (a[k] !== b[k]) return false
  for (const k of Object.keys(b)) if (a[k] !== b[k]) return false
  return true
}

function extractErr(e) {
  const m = (e?.message || '').match(/:\s*\{?"?error"?"?:\s*"?([^"}\n]+)/)
  return m ? m[1] : (e?.message || 'error')
}

function fmtFull(unix) {
  if (!unix) return '—'
  const d = new Date(unix * 1000)
  return d.toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('th-TH-u-ca-gregory', { hour: '2-digit', minute: '2-digit' })
}
